import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Switch,
  LinearProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  MoreHoriz as MoreIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Check as CheckIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';

// ─── Theme color ───
const ACCENT = '#6D53F4';
const ACCENT_HOVER = '#5A3FE0';
const ACCENT_LIGHT = '#F0EDFE';

// ─── Mock Data ───
const mockPlans = [
  {
    id: 1,
    name: 'Free',
    price: 0,
    currency: '₹',
    duration: '7 Days',
    trialPeriod: '7 Days',
    subscribers: 12420,
    status: 'Active',
  },
  {
    id: 2,
    name: 'Plus',
    price: 299,
    currency: '₹',
    duration: 'Monthly',
    trialPeriod: '—',
    subscribers: 3240,
    status: 'Active',
  },
  {
    id: 3,
    name: 'Pro',
    price: 1399,
    currency: '₹',
    duration: 'Monthly',
    trialPeriod: '—',
    subscribers: 820,
    status: 'Active',
  },
];

const mockFeatures = [
  {
    id: 1,
    name: 'Profile Views',
    free: '1/day',
    plus: '10/day',
    pro: 'Unlimited',
    status: true,
    freeType: 'limit',
    plusType: 'limit',
    proType: 'limit',
  },
  {
    id: 2,
    name: 'Direct Message',
    free: false,
    plus: '3/day',
    pro: 'Unlimited',
    status: true,
    freeType: 'boolean',
    plusType: 'limit',
    proType: 'limit',
  },
  {
    id: 3,
    name: 'Profile Views',
    subtitle: 'Off',
    free: '1/day',
    plus: '10/day',
    pro: 'Unlimited',
    status: false,
    freeType: 'limit',
    plusType: 'limit',
    proType: 'limit',
  },
  {
    id: 4,
    name: 'Verified Badges',
    free: false,
    plus: false,
    pro: true,
    status: true,
    freeType: 'boolean',
    plusType: 'boolean',
    proType: 'boolean',
  },
  {
    id: 5,
    name: 'Incognito Mode',
    free: false,
    plus: true,
    pro: true,
    status: true,
    freeType: 'boolean',
    plusType: 'boolean',
    proType: 'boolean',
  },
  {
    id: 6,
    name: 'Advanced Filters',
    free: false,
    plus: true,
    pro: true,
    status: true,
    freeType: 'boolean',
    plusType: 'boolean',
    proType: 'boolean',
  },
  {
    id: 7,
    name: 'Super Likes',
    free: false,
    plus: '1/day',
    pro: '5/day',
    status: true,
    freeType: 'boolean',
    plusType: 'limit',
    proType: 'limit',
  },
  {
    id: 8,
    name: 'Rewinds',
    free: false,
    plus: '1/day',
    pro: 'Unlimited',
    status: true,
    freeType: 'boolean',
    plusType: 'limit',
    proType: 'limit',
  },
  {
    id: 9,
    name: 'Event Promotion',
    free: false,
    plus: false,
    pro: true,
    status: true,
    freeType: 'boolean',
    plusType: 'boolean',
    proType: 'boolean',
  },
  {
    id: 10,
    name: 'Profile Insights',
    free: false,
    plus: true,
    pro: true,
    status: true,
    freeType: 'boolean',
    plusType: 'boolean',
    proType: 'boolean',
  },
];

// ─── Mock Coupons ───
const mockCoupons = [
  {
    id: 1,
    code: 'WELCOME30',
    description: 'New user welcome discount',
    discountValue: 30,
    discountType: 'Percentage',
    plan: 'Plus',
    usageCurrent: 312,
    usageLimit: 500,
    expiry: '31 Jul 2026',
    status: 'Active',
  },
  {
    id: 2,
    code: 'FLAT100',
    description: 'Flat ₹100 off on Pro',
    discountValue: 100,
    discountType: 'Flat',
    plan: 'Pro',
    usageCurrent: 200,
    usageLimit: 200,
    expiry: '15 May 2026',
    status: 'Expired',
  },
  {
    id: 3,
    code: 'SUMMER50',
    description: 'Summer sale — 50% off',
    discountValue: 50,
    discountType: 'Percentage',
    plan: 'Plus',
    usageCurrent: 87,
    usageLimit: 1000,
    expiry: '30 Jun 2026',
    status: 'Scheduled',
  },
  {
    id: 4,
    code: 'PRO20',
    description: 'Pro upgrade promo',
    discountValue: 20,
    discountType: 'Percentage',
    plan: 'Pro',
    usageCurrent: 241,
    usageLimit: 300,
    expiry: '10 Aug 2026',
    status: 'Active',
  },
  {
    id: 5,
    code: 'FREEMIUM',
    description: 'Free to Plus first month',
    discountValue: 100,
    discountType: 'Percentage',
    plan: 'Plus',
    usageCurrent: 150,
    usageLimit: 150,
    expiry: '01 Apr 2026',
    status: 'Expired',
  },
  {
    id: 6,
    code: 'LAUNCH25',
    description: 'Launch week special',
    discountValue: 25,
    discountType: 'Percentage',
    plan: 'All',
    usageCurrent: 648,
    usageLimit: 2000,
    expiry: '20 Sep 2026',
    status: 'Active',
  },
];

// ─── Sub-tab definitions ───
const SUB_TABS = ['Plans', 'Features', 'Coupons', 'Analytics', 'Settings'];
const PERIOD_OPTIONS = ['7 Days', 'Monthly', 'Yearly'];

// ─── Status color helper ───
const getCouponStatusStyle = (status) => {
  switch (status) {
    case 'Active':
      return { color: '#16A34A', dotColor: '#16A34A' };
    case 'Expired':
      return { color: '#DC2626', dotColor: '#DC2626' };
    case 'Scheduled':
      return { color: '#2563EB', dotColor: '#2563EB' };
    default:
      return { color: '#6B7280', dotColor: '#6B7280' };
  }
};

// ─── Helper: render feature cell value ───
const FeatureValue = ({ value, type }) => {
  if (type === 'boolean') {
    if (value === true) {
      return (
        <Box
          sx={{
            width: 24,
            height: 24,
            borderRadius: '50%',
            bgcolor: ACCENT,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
          }}
        >
          <CheckIcon sx={{ color: '#fff', fontSize: 16 }} />
        </Box>
      );
    }
    return (
      <CloseIcon sx={{ color: '#D1D5DB', fontSize: 20, display: 'block', mx: 'auto' }} />
    );
  }
  // limit type — render a chip-like label
  if (typeof value === 'string') {
    const isUnlimited = value === 'Unlimited';
    return (
      <Chip
        label={value}
        size="small"
        sx={{
          bgcolor: isUnlimited ? '#EDE9FE' : '#F0FDF4',
          color: isUnlimited ? ACCENT : '#16A34A',
          fontWeight: 600,
          fontSize: '0.75rem',
          height: 26,
          borderRadius: '6px',
          border: 'none',
        }}
      />
    );
  }
  return <CloseIcon sx={{ color: '#D1D5DB', fontSize: 20, display: 'block', mx: 'auto' }} />;
};

const SubscriptionManagement = () => {
  const [activeTab, setActiveTab] = useState('Plans');
  const [activePeriod, setActivePeriod] = useState('Monthly');
  const [searchQuery, setSearchQuery] = useState('');
  const [couponSearchQuery, setCouponSearchQuery] = useState('');
  const [features, setFeatures] = useState(mockFeatures);

  const activeFeatures = features.filter((f) => f.status);

  const handleToggleFeatureStatus = (id) => {
    setFeatures((prev) =>
      prev.map((f) => (f.id === id ? { ...f, status: !f.status } : f))
    );
  };

  const filteredPlans = mockPlans.filter((plan) =>
    plan.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // ─── Render Plans Tab ───
  const renderPlansTab = () => (
    <Box>
      {/* Plans Overview */}
      <Paper
        elevation={0}
        sx={{
          border: '1px solid #E5E7EB',
          borderRadius: '12px',
          mb: 4,
          overflow: 'hidden',
        }}
      >
        {/* Section Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            py: 2.5,
            borderBottom: '1px solid #F3F4F6',
          }}
        >
          <Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, fontSize: '1.05rem', color: '#111827' }}
            >
              Plans Overview
            </Typography>
            <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.3 }}>
              View and manage all subscription plans.
            </Typography>
          </Box>
          <TextField
            placeholder="Search plans..."
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{
              width: 220,
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                bgcolor: '#F9FAFB',
                '& fieldset': { borderColor: '#E5E7EB' },
                '&:hover fieldset': { borderColor: ACCENT },
                '&.Mui-focused fieldset': { borderColor: ACCENT },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#9CA3AF', fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Plans Table */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={thStyle}>Plan Name</TableCell>
                <TableCell sx={thStyle}>Price</TableCell>
                <TableCell sx={thStyle}>Duration</TableCell>
                <TableCell sx={thStyle}>Trial Period</TableCell>
                <TableCell sx={thStyle}>Subscribers</TableCell>
                <TableCell sx={thStyle}>Status</TableCell>
                <TableCell sx={thStyle}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPlans.map((plan) => (
                <TableRow
                  key={plan.id}
                  sx={{
                    '&:hover': { bgcolor: '#FAFAFE' },
                    '& td': {
                      borderBottom: '1px solid #F3F4F6',
                      py: 2,
                      fontSize: '0.875rem',
                    },
                  }}
                >
                  <TableCell>
                    <Typography sx={{ fontWeight: 600, color: '#111827' }}>
                      {plan.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontWeight: 500, color: '#374151' }}>
                      {plan.currency}
                      {plan.price.toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ color: '#6B7280' }}>{plan.duration}</TableCell>
                  <TableCell sx={{ color: '#6B7280' }}>{plan.trialPeriod}</TableCell>
                  <TableCell>
                    <Typography sx={{ fontWeight: 500, color: '#374151' }}>
                      {plan.subscribers.toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: '#16A34A',
                        }}
                      />
                      <Typography
                        sx={{ color: '#16A34A', fontWeight: 600, fontSize: '0.8rem' }}
                      >
                        {plan.status}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Button
                        size="small"
                        startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                        sx={{
                          textTransform: 'none',
                          color: '#6B7280',
                          fontWeight: 500,
                          fontSize: '0.8rem',
                          minWidth: 'auto',
                          px: 1.5,
                          '&:hover': { color: ACCENT, bgcolor: ACCENT_LIGHT },
                        }}
                      >
                        Edit
                      </Button>
                      <IconButton
                        size="small"
                        sx={{
                          color: '#9CA3AF',
                          '&:hover': { color: ACCENT, bgcolor: ACCENT_LIGHT },
                        }}
                      >
                        <MoreIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Feature Comparison */}
      <Paper
        elevation={0}
        sx={{
          border: '1px solid #E5E7EB',
          borderRadius: '12px',
          overflow: 'hidden',
        }}
      >
        {/* Section Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            py: 2.5,
            borderBottom: '1px solid #F3F4F6',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, fontSize: '1.05rem', color: '#111827' }}
            >
              Feature Comparison
            </Typography>
            <Chip
              label={`${activeFeatures.length}/${features.length} active`}
              size="small"
              sx={{
                bgcolor: '#ECFDF5',
                color: '#059669',
                fontWeight: 600,
                fontSize: '0.7rem',
                height: 24,
                borderRadius: '6px',
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ color: '#6B7280' }}>
              Compare features and limits across all plans.
            </Typography>
            <Button
              size="small"
              startIcon={<AddIcon sx={{ fontSize: 16 }} />}
              sx={{
                textTransform: 'none',
                color: '#374151',
                fontWeight: 600,
                fontSize: '0.8rem',
                ml: 2,
                '&:hover': { color: ACCENT, bgcolor: ACCENT_LIGHT },
              }}
            >
              Add Feature
            </Button>
          </Box>
        </Box>

        {/* Feature Table */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...thStyle, width: '22%' }}>Feature</TableCell>
                <TableCell sx={{ ...thStyle, textAlign: 'center', width: '13%' }}>
                  Free
                </TableCell>
                <TableCell sx={{ ...thStyle, textAlign: 'center', width: '13%' }}>
                  Plus
                </TableCell>
                <TableCell sx={{ ...thStyle, textAlign: 'center', width: '13%' }}>
                  Pro
                </TableCell>
                <TableCell sx={{ ...thStyle, width: '16%' }}>Status</TableCell>
                <TableCell sx={{ ...thStyle, width: '10%' }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {features.map((feature) => (
                <TableRow
                  key={feature.id}
                  sx={{
                    '&:hover': { bgcolor: '#FAFAFE' },
                    '& td': {
                      borderBottom: '1px solid #F3F4F6',
                      py: 1.8,
                    },
                  }}
                >
                  <TableCell>
                    <Box>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          color: '#111827',
                          fontSize: '0.875rem',
                        }}
                      >
                        {feature.name}
                      </Typography>
                      {feature.subtitle && (
                        <Chip
                          label={feature.subtitle}
                          size="small"
                          sx={{
                            mt: 0.5,
                            bgcolor: '#F3F4F6',
                            color: '#9CA3AF',
                            fontWeight: 500,
                            fontSize: '0.65rem',
                            height: 20,
                            borderRadius: '4px',
                          }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>
                    <FeatureValue value={feature.free} type={feature.freeType} />
                  </TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>
                    <FeatureValue value={feature.plus} type={feature.plusType} />
                  </TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>
                    <FeatureValue value={feature.pro} type={feature.proType} />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Switch
                        checked={feature.status}
                        onChange={() => handleToggleFeatureStatus(feature.id)}
                        size="small"
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: ACCENT,
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            bgcolor: ACCENT,
                          },
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: '0.8rem',
                          fontWeight: 600,
                          color: feature.status ? '#16A34A' : '#9CA3AF',
                        }}
                      >
                        {feature.status ? 'Active' : 'Inactive'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      startIcon={<EditIcon sx={{ fontSize: 14 }} />}
                      sx={{
                        textTransform: 'none',
                        color: '#6B7280',
                        fontWeight: 500,
                        fontSize: '0.78rem',
                        minWidth: 'auto',
                        px: 1,
                        '&:hover': { color: ACCENT, bgcolor: ACCENT_LIGHT },
                      }}
                    >
                      Edit
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Footer note */}
        <Box sx={{ px: 3, py: 2, borderTop: '1px solid #F3F4F6' }}>
          <Typography variant="body2" sx={{ color: '#9CA3AF', fontSize: '0.78rem' }}>
            Toggle features on or off to control visibility across all plans.
          </Typography>
        </Box>
      </Paper>
    </Box>
  );

  // ─── Coupon helpers ───
  const filteredCoupons = mockCoupons.filter(
    (c) =>
      c.code.toLowerCase().includes(couponSearchQuery.toLowerCase()) ||
      c.description.toLowerCase().includes(couponSearchQuery.toLowerCase())
  );
  const totalCoupons = mockCoupons.length;
  const activeCoupons = mockCoupons.filter((c) => c.status === 'Active').length;
  const expiredCoupons = mockCoupons.filter((c) => c.status === 'Expired').length;
  const totalRedemptions = mockCoupons.reduce((sum, c) => sum + c.usageCurrent, 0);

  // ─── Render Coupons Tab ───
  const renderCouponsTab = () => (
    <Box>
      {/* Stats Cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        {[
          { label: 'Total Coupons', value: totalCoupons, color: ACCENT },
          { label: 'Active', value: activeCoupons, color: '#16A34A' },
          { label: 'Expired', value: expiredCoupons, color: '#DC2626' },
          { label: 'Total Redemptions', value: totalRedemptions, color: ACCENT },
        ].map((stat) => (
          <Paper
            key={stat.label}
            elevation={0}
            sx={{
              flex: 1,
              border: '1px solid #E5E7EB',
              borderRadius: '12px',
              p: 2.5,
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: 4,
                bgcolor: stat.color,
                borderRadius: '12px 0 0 12px',
              },
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: '#6B7280', fontSize: '0.78rem', mb: 0.5 }}
            >
              {stat.label}
            </Typography>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: '1.75rem',
                color: '#111827',
                lineHeight: 1.2,
              }}
            >
              {stat.value.toLocaleString()}
            </Typography>
          </Paper>
        ))}
      </Box>

      {/* Coupon Codes Table */}
      <Paper
        elevation={0}
        sx={{
          border: '1px solid #E5E7EB',
          borderRadius: '12px',
          overflow: 'hidden',
        }}
      >
        {/* Section Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            py: 2.5,
            borderBottom: '1px solid #F3F4F6',
          }}
        >
          <Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, fontSize: '1.05rem', color: '#111827' }}
            >
              Coupon Codes
            </Typography>
            <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.3 }}>
              Create and manage discount coupons for subscription plans.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <TextField
              placeholder="Search coupons..."
              size="small"
              value={couponSearchQuery}
              onChange={(e) => setCouponSearchQuery(e.target.value)}
              sx={{
                width: 200,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  bgcolor: '#F9FAFB',
                  '& fieldset': { borderColor: '#E5E7EB' },
                  '&:hover fieldset': { borderColor: ACCENT },
                  '&.Mui-focused fieldset': { borderColor: ACCENT },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#9CA3AF', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              size="small"
              sx={{
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 600,
                px: 2,
                py: 1,
                fontSize: '0.82rem',
                bgcolor: ACCENT,
                color: '#fff',
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: ACCENT_HOVER,
                  boxShadow: '0 2px 8px rgba(109, 83, 244, 0.3)',
                },
              }}
            >
              Create Coupon
            </Button>
          </Box>
        </Box>

        {/* Coupon Table */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={thStyle}>Code</TableCell>
                <TableCell sx={thStyle}>Description</TableCell>
                <TableCell sx={thStyle}>Discount</TableCell>
                <TableCell sx={thStyle}>Plan</TableCell>
                <TableCell sx={{ ...thStyle, width: '14%' }}>Usage</TableCell>
                <TableCell sx={thStyle}>Expiry</TableCell>
                <TableCell sx={thStyle}>Status</TableCell>
                <TableCell sx={thStyle}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredCoupons.map((coupon) => {
                const statusStyle = getCouponStatusStyle(coupon.status);
                const usagePercent = (coupon.usageCurrent / coupon.usageLimit) * 100;
                const progressColor =
                  usagePercent >= 100
                    ? '#DC2626'
                    : usagePercent >= 75
                    ? '#F59E0B'
                    : ACCENT;

                return (
                  <TableRow
                    key={coupon.id}
                    sx={{
                      '&:hover': { bgcolor: '#FAFAFE' },
                      '& td': {
                        borderBottom: '1px solid #F3F4F6',
                        py: 2,
                        fontSize: '0.875rem',
                      },
                    }}
                  >
                    {/* Code */}
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Chip
                          label={coupon.code}
                          size="small"
                          sx={{
                            fontWeight: 700,
                            fontSize: '0.72rem',
                            letterSpacing: '0.04em',
                            color: ACCENT,
                            bgcolor: '#fff',
                            border: `1.5px solid ${ACCENT}`,
                            borderRadius: '6px',
                            height: 28,
                          }}
                        />
                        <IconButton
                          size="small"
                          sx={{
                            p: 0.3,
                            color: '#9CA3AF',
                            '&:hover': { color: ACCENT },
                          }}
                        >
                          <CopyIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Box>
                    </TableCell>

                    {/* Description */}
                    <TableCell>
                      <Typography
                        sx={{
                          color: '#374151',
                          fontSize: '0.84rem',
                          maxWidth: 180,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {coupon.description}
                      </Typography>
                    </TableCell>

                    {/* Discount */}
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                        <Typography
                          sx={{
                            fontSize: '0.84rem',
                            color: '#6B7280',
                          }}
                        >
                          ✂
                        </Typography>
                        <Typography
                          sx={{ fontWeight: 600, color: '#111827', fontSize: '0.84rem' }}
                        >
                          {coupon.discountType === 'Flat'
                            ? `₹${coupon.discountValue}`
                            : `${coupon.discountValue}%`}
                        </Typography>
                        <Typography
                          sx={{ color: '#9CA3AF', fontSize: '0.72rem' }}
                        >
                          ({coupon.discountType})
                        </Typography>
                      </Box>
                    </TableCell>

                    {/* Plan */}
                    <TableCell>
                      <Chip
                        label={coupon.plan}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          fontSize: '0.72rem',
                          bgcolor: '#F3F4F6',
                          color: '#374151',
                          height: 26,
                          borderRadius: '6px',
                        }}
                      />
                    </TableCell>

                    {/* Usage */}
                    <TableCell>
                      <Box sx={{ width: '100%' }}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            mb: 0.5,
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontSize: '0.8rem',
                              color: '#111827',
                            }}
                          >
                            {coupon.usageCurrent.toLocaleString()}
                          </Typography>
                          <Typography
                            sx={{ color: '#9CA3AF', fontSize: '0.75rem' }}
                          >
                            / {coupon.usageLimit.toLocaleString()}
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(usagePercent, 100)}
                          sx={{
                            height: 5,
                            borderRadius: 3,
                            bgcolor: '#F3F4F6',
                            '& .MuiLinearProgress-bar': {
                              bgcolor: progressColor,
                              borderRadius: 3,
                            },
                          }}
                        />
                      </Box>
                    </TableCell>

                    {/* Expiry */}
                    <TableCell>
                      <Typography sx={{ color: '#6B7280', fontSize: '0.82rem' }}>
                        {coupon.expiry}
                      </Typography>
                    </TableCell>

                    {/* Status */}
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: statusStyle.dotColor,
                          }}
                        />
                        <Typography
                          sx={{
                            color: statusStyle.color,
                            fontWeight: 600,
                            fontSize: '0.8rem',
                          }}
                        >
                          {coupon.status}
                        </Typography>
                      </Box>
                    </TableCell>

                    {/* Actions */}
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                        <Button
                          size="small"
                          startIcon={<EditIcon sx={{ fontSize: 14 }} />}
                          sx={{
                            textTransform: 'none',
                            color: '#6B7280',
                            fontWeight: 500,
                            fontSize: '0.78rem',
                            minWidth: 'auto',
                            px: 1,
                            '&:hover': { color: ACCENT, bgcolor: ACCENT_LIGHT },
                          }}
                        >
                          Edit
                        </Button>
                        <IconButton
                          size="small"
                          sx={{
                            color: '#D1D5DB',
                            '&:hover': { color: '#DC2626', bgcolor: '#FEF2F2' },
                          }}
                        >
                          <DeleteIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );

  // ─── Render placeholder for other tabs ───
  const renderPlaceholderTab = (tabName) => (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid #E5E7EB',
        borderRadius: '12px',
        p: 6,
        textAlign: 'center',
      }}
    >
      <Box
        sx={{
          width: 72,
          height: 72,
          borderRadius: '50%',
          bgcolor: ACCENT_LIGHT,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mx: 'auto',
          mb: 2,
        }}
      >
        <Typography sx={{ fontSize: '2rem' }}>
          {tabName === 'Features'
            ? '✨'
            : tabName === 'Analytics'
            ? '📊'
            : '⚙️'}
        </Typography>
      </Box>
      <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827', mb: 1 }}>
        {tabName}
      </Typography>
      <Typography variant="body2" sx={{ color: '#6B7280', maxWidth: 400, mx: 'auto' }}>
        {tabName === 'Features' &&
          'Manage subscription features and their availability across different plans.'}
        {tabName === 'Analytics' &&
          'View detailed analytics on subscription performance, revenue, and user engagement.'}
        {tabName === 'Settings' &&
          'Configure subscription settings, payment gateways, and notification preferences.'}
      </Typography>
      <Typography
        variant="body2"
        sx={{ color: '#9CA3AF', mt: 2, fontStyle: 'italic', fontSize: '0.78rem' }}
      >
        Coming soon
      </Typography>
    </Paper>
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* ─── Page Header ─── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          mb: 1,
        }}
      >
        <Box>
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, color: '#111827', fontSize: '1.5rem' }}
          >
            Subscription Management
          </Typography>
          <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.3 }}>
            Manage subscription plans, features and pricing.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          sx={{
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            py: 1.2,
            fontSize: '0.9rem',
            bgcolor: ACCENT,
            color: '#fff',
            boxShadow: `0 4px 14px rgba(109, 83, 244, 0.35)`,
            '&:hover': {
              bgcolor: ACCENT_HOVER,
              boxShadow: `0 6px 20px rgba(109, 83, 244, 0.45)`,
            },
          }}
        >
          {activeTab === 'Coupons' ? 'Create Coupon' : 'Create Plan'}
        </Button>
      </Box>

      {/* ─── Tabs + Period Toggle ─── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid #E5E7EB',
          mb: 3,
        }}
      >
        {/* Sub-tabs */}
        <Box sx={{ display: 'flex', gap: 0 }}>
          {SUB_TABS.map((tab) => (
            <Button
              key={tab}
              onClick={() => setActiveTab(tab)}
              sx={{
                textTransform: 'none',
                fontWeight: activeTab === tab ? 700 : 500,
                fontSize: '0.9rem',
                px: 2.5,
                py: 1.5,
                borderRadius: 0,
                color: activeTab === tab ? ACCENT : '#6B7280',
                borderBottom:
                  activeTab === tab
                    ? `2.5px solid ${ACCENT}`
                    : '2.5px solid transparent',
                '&:hover': {
                  bgcolor: 'transparent',
                  color: ACCENT,
                },
                transition: 'all 0.2s ease',
              }}
            >
              {tab}
            </Button>
          ))}
        </Box>

        {/* Period toggles — only on Plans tab */}
        {activeTab === 'Plans' && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              bgcolor: '#F3F4F6',
              borderRadius: '8px',
              p: '3px',
              mb: 0.5,
            }}
          >
            {PERIOD_OPTIONS.map((period) => (
              <Button
                key={period}
                onClick={() => setActivePeriod(period)}
                size="small"
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.78rem',
                  px: 2,
                  py: 0.6,
                  borderRadius: '6px',
                  minWidth: 'auto',
                  color: activePeriod === period ? '#fff' : '#6B7280',
                  bgcolor: activePeriod === period ? ACCENT : 'transparent',
                  boxShadow:
                    activePeriod === period
                      ? '0 2px 6px rgba(109, 83, 244, 0.3)'
                      : 'none',
                  '&:hover': {
                    bgcolor: activePeriod === period ? ACCENT_HOVER : '#E5E7EB',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                {period}
              </Button>
            ))}
          </Box>
        )}
      </Box>

      {/* ─── Tab Content ─── */}
      {activeTab === 'Plans' && renderPlansTab()}
      {activeTab === 'Coupons' && renderCouponsTab()}
      {!['Plans', 'Coupons'].includes(activeTab) && renderPlaceholderTab(activeTab)}
    </Box>
  );
};

// ─── Shared table header style ───
const thStyle = {
  fontWeight: 600,
  color: '#6B7280',
  fontSize: '0.8rem',
  textTransform: 'uppercase',
  letterSpacing: '0.03em',
  borderBottom: '1px solid #E5E7EB',
  bgcolor: '#FAFAFA',
  py: 1.5,
};

export default SubscriptionManagement;
